import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { Box, Dialog, DialogContent } from "@mui/material";
import { DialogFooter, DialogHeader, PremiumStepper } from "./components/DialogChrome";
import { CUSTOMER_CARD_STEPS } from "./constants/steps";
import useCustomerCardDialog from "./hooks/useCustomerCardDialog";
import useCustomerCardDialogImproved from "./hooks/useCustomerCardDialogImproved";
import AddressStep from "./steps/AddressStep";
import BillingStep from "./steps/BillingStep";
import OrganizationStep from "./steps/OrganizationStep";
import ReviewStep from "./steps/ReviewStep";
import { ui } from "./styles";
import { useSnackbar } from "../../../../hooks";
import { useIntl } from "react-intl";

const CustomerCardDialog = ({
  companyId,  
  existingCard,
  onClose,
  onOpenOrganization,  
  open,
  organization,
  organizationOptions,
  cardId = 0,
  onSaveComplete
}) => {

  const improvedController = useCustomerCardDialogImproved(companyId);
  const snackbar = useSnackbar();
  const intl = useIntl();

  const controller = useCustomerCardDialog({    
    existingCard,    
    open,
    organization,
    organizationOptions,
  });

  const {
    activeStep,
    setActiveStep,
    fetchCard,
    draftCard: draft,
    fetchedCard: baseline,
    updateField,
    onReset,
    canSave,
    saveCard: handleSave,
    createEmptyCard,
    updateAddress,
    setAddressFieldValue,
    setAddressFieldSetting,
    moveAddressLine,
    setDeliverySameAsBilling,
    knownCompanies,
    knownCompaniesLoading,
    knownCompaniesError,
    fetchKnownCompanies,
    selectedOrganization,
    updateSelectedOrganization,
  } = improvedController;

  const {     
    masterOrganization,
    addressSourceLoading,
    addressSourceError,
    logoError,
    logoSrc,        
    handleLogoFile,
    handleResetLogo,    
  } = controller;

  useEffect(() => {
    if (!open || !companyId) {
      return;
    }

    if (cardId) {
      fetchCard(companyId, cardId);
    } else {
      createEmptyCard(companyId);
    }

    fetchKnownCompanies(companyId);

  }, [
    open,
    companyId,
    cardId,
    fetchCard,
      createEmptyCard,
      fetchKnownCompanies
  ]);

  const handleSaveClick = async () => {
    const result = await handleSave();

    if (result === null) {
      snackbar.error(intl.formatMessage({ id: "common.errorOnSaving" }));
      return;
    }

    snackbar.success(intl.formatMessage({ id: "admin.crm.customerCard.saved" }));
    onSaveComplete?.();
    onClose?.();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          width: {
            xs: "calc(100vw - 12px)",
            sm: "min(1000px, calc(100vw - 24px))",
          },
          maxWidth: "1000px !important",
          height: {
            xs: "calc(100dvh - 12px)",
            sm: "min(760px, calc(100dvh - 24px))",
          },
          maxHeight: {
            xs: "calc(100dvh - 12px)",
            sm: "min(760px, calc(100dvh - 24px))",
          },
          m: { xs: 0.75, sm: 1.5 },
          borderRadius: 2.25,
          border: `1px solid ${ui.border}`,
          bgcolor: ui.page,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          boxShadow:
            "0 24px 70px rgba(16,24,40,.18), 0 4px 14px rgba(16,24,40,.08)",
        },
      }}
      BackdropProps={{
        sx: {
          bgcolor: "rgba(16,24,40,.54)",
          backdropFilter: "blur(2px)",
        },
      }}
    >
      <DialogHeader
        companyName={draft?.companyName || ""}
        existingCard={existingCard}
        logoSrc={logoSrc}
        masterOrganizationName={masterOrganization?.name}
        onClose={onClose}
      />

      <DialogContent
        sx={{
          p: 0,
          bgcolor: ui.page,
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            px: { xs: 1.25, sm: 2 },
            pt: 1.05,
            pb: 0.9,
            flexShrink: 0,
          }}
        >
          <PremiumStepper activeStep={activeStep} />
        </Box>

        <Box
          sx={{
            px: { xs: 1.25, sm: 2 },
            pb: { xs: 1.1, sm: 1.4 },
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            scrollbarWidth: "thin",
            scrollbarColor: "#cbd2dc transparent",
            "&::-webkit-scrollbar": { width: 7 },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: "#cbd2dc",
              borderRadius: 8,
              border: "2px solid transparent",
              backgroundClip: "content-box",
            },
          }}
        >
          <Box
            sx={{
              minHeight: "100%",
              border: `1px solid ${ui.border}`,
              borderRadius: 1.75,
              bgcolor: "#fff",
              boxShadow: "0 1px 2px rgba(16,24,40,.025)",
              overflow: "hidden",
            }}
          >            
            <Box sx={{ p: { xs: 1.15, sm: 1.35 } }}>
              {activeStep === 0 && (
                
                <OrganizationStep
                  availableOrganizations={knownCompanies}
                  baseline={baseline}
                  draft={draft}
                  logoError={logoError}
                  logoSrc={logoSrc}
                  onLogoFile={handleLogoFile}
                  onOpenOrganization={onOpenOrganization}
                  onOrganizationChange={updateSelectedOrganization}
                  onResetLogo={handleResetLogo}
                  addressSourceError={addressSourceError}
                  addressSourceLoading={addressSourceLoading}
                  selectedOrganization={selectedOrganization}
                  updateField={updateField}
                  onReset={onReset}
                />
              )}
              {activeStep === 1 && (
                <AddressStep
                  fetchedCard={baseline}
                  draftCard={draft}
                  moveAddressLine={moveAddressLine}
                  setAddressFieldSetting={setAddressFieldSetting}
                  setAddressFieldValue={setAddressFieldValue}
                  setDeliverySameAsBilling={setDeliverySameAsBilling}
                  updateAddress={updateAddress}
                />
              )}
              {activeStep === 2 && (
                <BillingStep
                  baseline={baseline}
                  draft={draft}
                  selectedOrganization={masterOrganization}
                  updateField={updateField}
                />
              )}
              {activeStep === 3 && (
                <ReviewStep baseline={baseline} draft={draft} logoSrc={logoSrc} />
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogFooter
        activeStep={activeStep}
        canSave={canSave}
        existingCard={draft.id !== 0}
        isLastStep={activeStep === CUSTOMER_CARD_STEPS.length - 1}
        onClose={onClose}
        onSave={handleSaveClick }
        setActiveStep={setActiveStep}
      />
    </Dialog>
  );
};

CustomerCardDialog.propTypes = {
  addressSourceGroupId: PropTypes.number,
  existingCard: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onOpenOrganization: PropTypes.func,
  
  open: PropTypes.bool.isRequired,
  organization: PropTypes.object,
  organizationOptions: PropTypes.arrayOf(PropTypes.object),
};

CustomerCardDialog.defaultProps = {
  existingCard: null,
  onOpenOrganization: null,
  organization: null,
  organizationOptions: [],
};

export default CustomerCardDialog;
